import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PageLayout from "../components/shared/PageLayout";

const History = () => {
  return (
    <>
      <Helmet>
        <title>History of Fortune Cookies | Origin and Evolution</title>
        <meta name="description" content="Discover the fascinating history and origin of fortune cookies, from their surprising beginnings to their global popularity today." />
        <meta name="keywords" content="fortune cookie history, origin of fortune cookies, fortune cookie facts, fortune cookie invention, traditional fortune cookies" />
        <meta property="og:title" content="History of Fortune Cookies | Origin and Evolution" />
        <meta property="og:description" content="Explore the surprising origins and fascinating evolution of fortune cookies from ancient China to modern American Chinese restaurants." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="/history" />
        <meta property="og:image" content="/fortune-cookie-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="History of Fortune Cookies | Origin and Evolution" />
        <meta name="twitter:description" content="Explore the surprising origins and fascinating evolution of fortune cookies from ancient China to modern American Chinese restaurants." />
        <meta name="twitter:image" content="/fortune-cookie-og.png" />
        <link rel="canonical" href="/history" />
      </Helmet>

      <PageLayout>
        <div className="max-w-4xl mx-auto w-full mb-16">
          <motion.div 
            className="space-y-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <motion.h1 
                className="text-4xl font-light mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                The Surprising History of Fortune Cookies
              </motion.h1>
              <motion.p
                className="text-muted-foreground text-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                From ancient origins to modern phenomenon
              </motion.p>
            </div>

            <motion.section 
              className="prose prose-slate max-w-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h2 className="text-2xl font-medium mb-4">A Japanese Beginning, Not Chinese</h2>
              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <p className="text-muted-foreground mb-4">
                    Despite being synonymous with Chinese cuisine in America, fortune cookies actually originated in Japan. The traditional Japanese crackers, called "tsujiura senbei" or "omikuji senbei," date back to the 19th century and were made by hand using iron molds over coals.
                  </p>
                  <p className="text-muted-foreground">
                    These Japanese crackers were larger and darker in color than today's fortune cookies, with messages placed in the bend rather than inside the hollow portion. They were sold in temples and shrines rather than restaurants.
                  </p>
                </div>
                <div className="bg-muted rounded-xl overflow-hidden">
                  <img 
                    src="/lovable-uploads/3381c55c-2f9a-4acd-9c77-02951c7614d4.png" 
                    alt="Traditional Japanese fortune cookie making and the evolution to modern fortune cookies" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>

              <h2 className="text-2xl font-medium mb-4">The American Transformation</h2>
              <p className="text-muted-foreground mb-4">
                Fortune cookies made their way to America in the early 1900s. Multiple origin stories exist about their introduction to the United States:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground mb-6 space-y-2">
                <li>Makoto Hagiwara, a Japanese immigrant who designed the Japanese Tea Garden in San Francisco's Golden Gate Park, is often credited with introducing fortune cookies to the United States around 1914.</li>
                <li>David Jung, founder of the Hong Kong Noodle Company in Los Angeles, claims to have invented the cookie in 1918 as a treat for the poor.</li>
                <li>Sally Osaki suggests that the cookies were made by Japanese bakers in San Francisco who sold them to Chinese restaurants where they gained popularity.</li>
              </ul>

              <div className="bg-secondary/50 p-6 rounded-lg my-8 border border-secondary">
                <h3 className="text-xl font-medium mb-2">World War II: A Pivotal Moment</h3>
                <p className="text-muted-foreground">
                  The fortune cookie industry changed dramatically during World War II. When Japanese Americans were forcibly relocated to internment camps, Chinese entrepreneurs filled the void, taking over the fortune cookie industry. This period cemented the cookies' association with Chinese American cuisine rather than Japanese culture.
                </p>
              </div>

              <h2 className="text-2xl font-medium mb-4">Mass Production & Global Spread</h2>
              <p className="text-muted-foreground mb-4">
                The fortune cookie industry was revolutionized in 1964 when Edward Louie of San Francisco's Lotus Fortune Cookie Company invented a machine that automated the folding process. This innovation allowed for mass production, helping fortune cookies spread from the West Coast throughout the United States and eventually around the world.
              </p>
              <p className="text-muted-foreground mb-8">
                Today, over 3 billion fortune cookies are made each year, primarily in the United States. Ironically, fortune cookies remain virtually unknown in China and are considered an American curiosity when introduced there.
              </p>

              <h2 className="text-2xl font-medium mb-4">Fortune Cookie Wisdom</h2>
              <p className="text-muted-foreground mb-4">
                The messages inside fortune cookies have evolved over time. Early fortunes focused on proverbial wisdom and prophecies, often including Chinese sayings translated into English (sometimes with amusing mistranslations).
              </p>
              <p className="text-muted-foreground mb-4">
                Modern fortunes tend to be optimistic predictions, vague advice, or inspirational messages. Some contain lucky numbers, lottery numbers, or even Chinese vocabulary lessons. The fortunes are typically written by professional writers hired by fortune cookie companies.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 my-8">
                <div className="bg-muted p-4 rounded-lg italic text-center">
                  "Your road to glory will be rocky, but fulfilling."
                </div>
                <div className="bg-muted p-4 rounded-lg italic text-center">
                  "A journey of a thousand miles begins with a single step."
                </div>
                <div className="bg-muted p-4 rounded-lg italic text-center">
                  "Happiness is around the next corner."
                </div>
              </div>

              <h2 className="text-2xl font-medium mb-4">Cultural Impact</h2>
              <p className="text-muted-foreground mb-4">
                Fortune cookies have become deeply embedded in American culture, appearing in movies, television shows, and literature. They've been featured in plots from romantic comedies to thrillers, sometimes as carriers of important clues or messages.
              </p>
              <p className="text-muted-foreground mb-8">
                They've also inspired marketing campaigns, art installations, and even specialized cookies for occasions like weddings and corporate events. Fortune cookie messages have influenced everything from lottery number selection to major life decisions for some believers.
              </p>

              <div className="bg-secondary/50 p-6 rounded-lg my-8 border border-secondary">
                <h3 className="text-xl font-medium mb-2">Did You Know?</h3>
                <ul className="list-disc pl-5 text-muted-foreground mb-0 space-y-2">
                  <li>The largest fortune cookie ever made was over 21 feet long and weighed nearly 1,900 pounds.</li>
                  <li>Fortune cookies are almost exclusively an American phenomenon and are rarely found in China.</li>
                  <li>Some fortune cookie companies offer customized messages for special events.</li>
                  <li>In 2005, 110 people won second prize in the Powerball lottery because they used numbers from fortune cookies.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-medium mb-4">Fortune Cookies Today</h2>
              <p className="text-muted-foreground mb-4">
                Modern fortune cookies have expanded beyond traditional Chinese restaurants. They're now available in various flavors including chocolate, strawberry, and even savory options. Some companies offer gluten-free, organic, and gourmet versions.
              </p>
              <p className="text-muted-foreground">
                Digital fortune cookies have also emerged, with websites and apps offering virtual fortune cookie experiences similar to our own. These digital adaptations keep the tradition alive in the internet age, allowing people to receive bite-sized wisdom with just a click or tap.
              </p>
            </motion.section>

            <div className="text-center pt-8">
              <Link to="/">
                <Button className="bg-cookie-dark hover:bg-cookie-light text-white">
                  Get Your Fortune Now
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </PageLayout>
    </>
  );
};

export default History;
